<script setup lang="ts">
/**
 * PracticeAllText.vue — Extracted from PracticeView.vue Phase 6d
 *
 * Contains the full text AnnotatedText view (all sentences, not just focus).
 * Receives parent's reactive state object to avoid prop drilling.
 */
import type { BarMitzvahType, SentenceType } from 'types/types'
import { computed } from 'vue'

import AnnotatedText from '@/components/AnnotatedText.vue'
import { useFont } from '@/composables/font'
import { useAnnotations } from '@/composables/useAnnotations'
import { ALL_TEXT_NAME } from '@/constants/practiceConstants'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import type { TransformationType } from '@/util/Transformations'

interface PracticeAllTextState {
  annotatedSentences: Array<unknown> // Array<AnnotatedSentence> — typed as unknown[] to avoid reactive() type expansion mismatch
  tikkunStyle: boolean
  fontSize: number
  sentences: Array<SentenceType>
  transformation: TransformationType | null
}

const props = defineProps<{
  state: PracticeAllTextState
  barMitzvah: BarMitzvahType | null
  sentencesHaveSameResponsiveness: boolean
  hasRecordingByAnnotatedSentence: (annotatedSentence: AnnotatedSentence) => boolean
  sentenceStyle: (id: string, annotatedSentence: AnnotatedSentence) => string
  lineHeight: number
  handleSentenceClick: (annotatedSentence: AnnotatedSentence, sentenceIndex: number) => void
}>()

const { font } = useFont()
const { annotations } = useAnnotations(computed(() => props.barMitzvah))
</script>

<template>
  <v-row class="mt-4">
    <v-col lg="12" md="12" xl="10">
      <v-card>
        <v-card-text>
          <v-row class="py-4 px-3" justify="center" no-gutters>
            <v-col md="11">
              <annotated-text
                v-model:annotated-sentences="state.annotatedSentences as Array<AnnotatedSentence>"
                :annotations="annotations"
                :break-before-line="
                  () => {
                    return !state.tikkunStyle || !sentencesHaveSameResponsiveness
                  }
                "
                :clickable-sentence="hasRecordingByAnnotatedSentence"
                :default-font="font"
                :font-size="state.fontSize"
                :line-height="lineHeight"
                :name="ALL_TEXT_NAME"
                :sentence-style="sentenceStyle"
                :sentences="state.sentences"
                :show-end-of-sentence-character="!state.tikkunStyle"
                :show-responsive-headers="
                  () => {
                    return !sentencesHaveSameResponsiveness
                  }
                "
                :show-sentence-numbers="!state.tikkunStyle"
                :suppress-colors="
                  () => {
                    return state.tikkunStyle
                  }
                "
                :transformation="state.transformation as TransformationType"
                @click-sentence="handleSentenceClick"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>
